import styled, { createGlobalStyle } from 'styled-components';
import { transparentize } from 'polished';

import { Theme } from '../../../themeBase';
import { fontSmoothing } from '../../../mixins';

export const DatePickerStyles = createGlobalStyle`
.react-calendar {
  width: 300px;
  max-width: 100%;
  background: ${({ theme }: { theme: Theme }) => theme.colors.white.main};
  border: none;
  line-height: 1.125em;
  font-family: ${({ theme }: { theme: Theme }) => theme.fonts.secondary};
  border-radius: ${({ theme }: { theme: Theme }) => theme.radii[3]}px;
  box-shadow: 0 12px 24px 0 ${({ theme }: { theme: Theme }) =>
    transparentize(0.85, theme.colors.black.dark)};
  user-select: none;

  ${fontSmoothing};
}

.react-calendar--doubleView {
  width: 700px;
}
.react-calendar--doubleView .react-calendar__viewContainer {
  display: flex;
  margin: -0.5em;
}
.react-calendar--doubleView .react-calendar__viewContainer > * {
  width: 50%;
  margin: 0.5em;
}
.react-calendar,
.react-calendar *,
.react-calendar *:before,
.react-calendar *:after {
  box-sizing: border-box;
}
.react-calendar button {
  margin: 0;
  border: 0;
  outline: none;
  font-family: ${({ theme }: { theme: Theme }) => theme.fonts.secondary};
}
.react-calendar button:enabled:hover {
  cursor: pointer;
}
.react-calendar__navigation {
  display: flex;
  height: auto;
  padding: 10px 20px;
  padding: ${({ theme }: { theme: Theme }) => theme.space[3]}px;
  border-bottom: 1px solid
    ${({ theme }: { theme: Theme }) => theme.colors.white.main};
  column-gap: 20px;
}
.react-calendar__navigation button {
  background: none;
  padding: 0;
  min-width: 20px;
}
.react-calendar__navigation button:enabled:hover,
.react-calendar__navigation button:enabled:focus-visible {
  background-color: #e6e6e6;
}
.react-calendar__navigation button[disabled] {
  background: none;
  pointer-events: none;
  opacity: 0.5;
}
.react-calendar__month-view__weekdays {
  text-align: center;
  color: ${({ theme }: { theme: Theme }) =>
    transparentize(0.3, theme.colors.black.dark)};
  font-size: 0.75rem;
  font-weight: 400;
  text-transform: none;

  abbr {
    text-decoration: none;
    border: none;
    cursor: default;
  }
}
.react-calendar__month-view__weekdays__weekday {
  padding: 10px 5px;
}
.react-calendar__month-view__weekNumbers .react-calendar__tile {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75em;
  font-weight: bold;
  padding: calc(0.75em / 0.75) calc(0.5em / 0.75);
}
.react-calendar__month-view__days__day--neighboringMonth {
  opacity: 0.3;
}
.react-calendar__year-view .react-calendar__tile,
.react-calendar__decade-view .react-calendar__tile,
.react-calendar__century-view .react-calendar__tile {
  padding: 10px 5px;
  border-radius: ${({ theme }: { theme: Theme }) => theme.radii[3]}px;
  font-size: 14px;
  border: ${({ theme }: { theme: Theme }) =>
    `2px solid ${theme.colors.white.main}`};
}
.react-calendar__century-view .react-calendar__tile {
  font-size: 13px;
}
.react-calendar__tile {
  max-width: 100%;
  text-align: center;
  padding: 0.75em 0.5em;
  background: none;
}
.react-calendar__tile:disabled {
  background-color: ${({ theme }: { theme: Theme }) =>
    theme.colors.primary.extraLight};
  pointer-events: none;
  opacity: 0.5;
}
.react-calendar__tile:enabled:hover,
.react-calendar__tile:enabled:focus-visible {
  background-color: #e6e6e6;
}
.react-calendar__tile--now {
  background: #ffff76;
}
.react-calendar__tile--now:enabled:hover,
.react-calendar__tile--now:enabled:focus-visible {
  background: #ffffa9;
}
.react-calendar__tile--hasActive {
  background: #76baff;
}
.react-calendar__tile--hasActive:enabled:hover,
.react-calendar__tile--hasActive:enabled:focus-visible {
  background: #a9d4ff;
}
.react-calendar--selectRange .react-calendar__tile--hover {
  background-color: #e6e6e6;
}

.react-calendar__month-view__days__day abbr {
  font-size: 14px;
  border-radius: 50%;
  width: ${({ theme }: { theme: Theme }) => theme.sizes[5]}px;
  height: ${({ theme }: { theme: Theme }) => theme.sizes[5]}px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.react-calendar__tile.react-calendar__month-view__days__day {
  padding: 0;
}

.react-calendar__month-view {
  padding: ${({ theme }: { theme: Theme }) => theme.space[3]}px;
  padding-top: 0;
}

.react-calendar__tile--now,
.react-calendar__tile:enabled:hover,
.react-calendar__tile:enabled:focus-visible,
.react-calendar__navigation__label:enabled:hover,
.react-calendar__navigation__label:enabled:focus-visible,
.react-calendar__navigation button:enabled:hover,
.react-calendar__navigation button:enabled:focus-visible,
.react-calendar__navigation button[disabled] {
  background: ${({ theme }: { theme: Theme }) =>
    theme.colors.primary.extraLight};
}

button.react-calendar__tile--active abbr,
button.react-calendar__tile--active:enabled:hover abbr,
button.react-calendar__tile--active:enabled:focus-visible
  abbr
  button.react-calendar__tile--active.react-calendar__month-view__days__day--weekend
  abbr,
button.react-calendar__tile--active.react-calendar__month-view__days__day--weekend:enabled:hover
  abbr,
button.react-calendar__tile--active.react-calendar__month-view__days__day--weekend:enabled:focus-visible
  abbr,
.react-calendar__tile.react-calendar__tile--active.react-calendar__month-view__days__day:hover
  abbr,
.react-calendar__tile.react-calendar__tile--active.react-calendar__month-view__days__day:focus-visible
  abbr,
.react-calendar__tile--hasActive,
.react-calendar__tile--hasActive:enabled:hover,
.react-calendar__tile--hasActive:enabled:focus-visible,
button.react-calendar__tile--hasActive.react-calendar__year-view__months__month,
button.react-calendar__tile--hasActive.react-calendar__year-view__months__month:enabled:hover,
button.react-calendar__tile--hasActive.react-calendar__year-view__months__month:enabled:focus-visible,
button.react-calendar__tile--hasActive.react-calendar__decade-view__years__year,
button.react-calendar__tile--hasActive.react-calendar__decade-view__years__year:enabled:hover,
button.react-calendar__tile--hasActive.react-calendar__decade-view__years__year:enabled:focus-visible {
  color: ${({ theme }: { theme: Theme }) => theme.colors.white.main};
  background: ${({ theme }: { theme: Theme }) => theme.colors.primary.main};
}

button.react-calendar__tile--hasActive.react-calendar__year-view__months__month,
button.react-calendar__tile--hasActive.react-calendar__year-view__months__month:enabled:hover,
button.react-calendar__tile--hasActive.react-calendar__year-view__months__month:enabled:focus-visible,
button.react-calendar__tile--hasActive.react-calendar__decade-view__years__year,
button.react-calendar__tile--hasActive.react-calendar__decade-view__years__year:enabled:hover,
button.react-calendar__tile--hasActive.react-calendar__decade-view__years__year:enabled:focus-visible {
  border: ${({ theme }: { theme: Theme }) =>
    `2px solid ${theme.colors.white.main}`};
}

.react-calendar__navigation__label__labelText {
  font-size: 1rem;
  color: ${({ theme }: { theme: Theme }) => theme.colors.primary.main};
}

button.react-calendar__navigation__arrow:enabled:hover,
button.react-calendar__navigation__arrow:enabled:focus-visible {
  background: none;
}

button.react-calendar__navigation__arrow:enabled:focus-visible {
  outline: ${({ theme }: { theme: Theme }) =>
    `1px solid ${transparentize(0.5, theme.colors.primary.main)}`};
}

button.react-calendar__navigation__label {
  padding: 10px;
  border-radius: ${({ theme }: { theme: Theme }) => theme.radii[3]}px;
  transition: background-color 250ms ease-in-out;
}

.react-calendar__year-view__months,
.react-calendar__decade-view__years,
.react-calendar__century-view__decades {
  padding: ${({ theme }: { theme: Theme }) => theme.space[3]}px;
  padding-top: 0;
}

.react-calendar__tile.react-calendar__month-view__days__day:hover,
.react-calendar__tile.react-calendar__month-view__days__day:focus-visible {
  background: none;

  abbr {
    background: ${({ theme }: { theme: Theme }) =>
      transparentize(0.9, theme.colors.primary.dark)};
  }
}

.react-calendar__tile--now.react-calendar__month-view__days__day {
  background: none;
  abbr {
    border: ${({ theme }: { theme: Theme }) =>
      `1px solid ${transparentize(0.5, theme.colors.primary.main)}`};
  }

  &:hover,
  &:focus-visible {
    abbr {
      background: ${({ theme }: { theme: Theme }) =>
        transparentize(0.9, theme.colors.primary.main)};
    }
  }
}

.react-calendar__tile--now.react-calendar__year-view__months__month:not(.react-calendar__tile--hasActive),
.react-calendar__tile--now.react-calendar__century-view__decades__decade:not(.react-calendar__tile--hasActive),
.react-calendar__tile--now.react-calendar__decade-view__years__year:not(.react-calendar__tile--hasActive) {
  background: none;
  outline: ${({ theme }: { theme: Theme }) =>
    `1px solid ${transparentize(0.5, theme.colors.primary.main)}`};
  outline-offset: -2px;

  &:hover,
  &:focus-visible {
    background: ${({ theme }: { theme: Theme }) =>
      transparentize(0.9, theme.colors.primary.main)};
  }
}

.react-calendar__tile--now.react-calendar__tile--active {
  abbr {
    background: ${({ theme }: { theme: Theme }) => theme.colors.primary.main};
  }
}

.react-calendar__month-view__weekdays abbr {
  font-weight: 800;
  text-transform: uppercase;
}
.react-calendar__month-view__days__day {
  &--weekend {
    color: ${({ theme }: { theme: Theme }) => theme.colors.danger.main};
  }
}
.react-calendar__tile--now.react-calendar__month-view__days__day abbr {
  border: ${({ theme }: { theme: Theme }) =>
    `1px solid ${theme.colors.primary.main}`};
}

.react-calendar__tile:not(.react-calendar__tile--now, .react-calendar__month-view__days__day--weekend, .react-calendar__tile--hasActive) {
  color: ${({ theme }: { theme: Theme }) => theme.colors.black.main};
}
`;

export const Container = styled.div`
  position: relative;
`;
